import { Box, Typography, Chip, TextField, Stack, Alert } from '@mui/material';
import { BaseAccordion } from '../../../../../Accordion/BaseAccordion';
import { ValidationBadge } from '../../ValidationBadge/ValidationBadge';
import {
  CustomSelect,
  OptionType,
} from '../../../../../Infra/CustomSelect/CustomSelect';
import { CustomButton } from '../../../../../Infra/CustomButton/CustomButton';
import { MathOperationsSectionProps } from './types';
import { useMathOperationsSection } from './useMathOperationsSection';
import './MathOperationsSection.scss';

export const MathOperationsSection = (props: MathOperationsSectionProps) => {
  const { title, expanded, onChange } = props;

  const {
    formula,
    variables,
    selectedColumn,
    nextVariableName,
    availableColumns,
    validation,
    formulaPreview,
    setSelectedColumn,
    addVariable,
    removeVariable,
    updateFormula,
    validationResult,
  } = useMathOperationsSection(props);

  const columnOptions: OptionType[] = availableColumns.map(col => ({
    value: col,
    label: col,
  }));

  const hasVariables = Object.keys(variables).length > 0;

  return (
    <BaseAccordion
      title={title}
      expanded={expanded}
      onChange={onChange}
      badge={<ValidationBadge validationResult={validationResult} />}
    >
      <Box className="mathOperationsSection">
        {/* Add Variable Section */}
        <Box className="addVariableSection">
          <Typography
            variant="subtitle2"
            className="sectionLabel"
            sx={{ textAlign: 'left' }}
          >
            Map Column to Variable
          </Typography>
          <Box className="addVariableControls">
            <CustomSelect
              placeholder="Select column"
              options={columnOptions}
              value={selectedColumn}
              onChange={value => setSelectedColumn(value as string)}
              size="sm"
            />
            <Typography variant="body2" className="arrow">
              →
            </Typography>
            <Chip
              label={nextVariableName}
              size="small"
              className={`nextVariableChip ${selectedColumn ? 'selected' : ''}`}
            />
            <CustomButton
              variant="contained"
              onClick={addVariable}
              disabled={!selectedColumn || availableColumns.length === 0}
              size="small"
            >
              Add Variable
            </CustomButton>
          </Box>
          {availableColumns.length === 0 && hasVariables && (
            <Alert severity="info" className="alert">
              All available columns have been assigned
            </Alert>
          )}
        </Box>

        {/* Current Variables */}
        {hasVariables && (
          <Box className="variablesDisplay">
            <Typography
              variant="subtitle2"
              className="sectionLabel"
              sx={{ textAlign: 'left' }}
            >
              Current Variables
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              className="variablesChips"
            >
              {Object.entries(variables).map(([varName, columnName]) => (
                <Chip
                  key={varName}
                  label={`${varName} = ${columnName}`}
                  onDelete={() => removeVariable(varName)}
                  className="variableChip"
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Formula Input */}
        <Box className="formulaInput">
          <Typography
            variant="subtitle2"
            className="sectionLabel"
            sx={{ textAlign: 'left' }}
          >
            Mathematical Formula
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={
              hasVariables
                ? `Enter formula using variables: ${Object.keys(variables).join(', ')}`
                : 'First add variables above, then create your formula'
            }
            value={formula}
            onChange={e => updateFormula(e.target.value)}
            disabled={!hasVariables}
            className="formulaTextField"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            className="helperText"
          >
            Supported operators: + (add), - (subtract), * (multiply), /
            (divide), ( ) (parentheses)
          </Typography>
        </Box>

        {/* Formula Preview */}
        {hasVariables && (
          <Box className="formulaPreview">
            <Typography
              variant="subtitle2"
              className="sectionLabel"
              sx={{ textAlign: 'left' }}
            >
              Formula Preview
            </Typography>
            <Box className="previewBox">
              <Typography
                variant="body2"
                className={`previewText ${!formula ? 'placeholder' : ''}`}
              >
                {formulaPreview}
              </Typography>
            </Box>
            {!validation.isValid && formula && (
              <Alert severity="error" className="alert">
                {validation.error}
              </Alert>
            )}
            {validation.isValid && formula && (
              <Alert severity="success" className="alert">
                Formula is valid and ready to use
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </BaseAccordion>
  );
};
